// 010B — Laboratorio Clínico Informe (Fase 7, Tanda 15, ver PLAN.md). Transcripción del PDF oficial del Anexo 1 MSP: hoja 1
// idx 45 (parámetros fijos) y hoja 2 idx 46 (17 secciones de filas libres), footer `SNS-MSP/HCU-form.010B/2021`. Informe
// de brechas en `docs/hce-fase7/brechas-010-012-013.md`.
//
// Retirado de la UI por no estar impreso: "Solicitante", "Fecha de entrega" y "Persona que recibe", y los 7 bloques de
// resultados que 010B importaba de 010A (no eran los del papel). Decisiones: cada fila es Examen / Resultado / Valor de referencia,
// más Unidad solo en Química (hoja 1, donde el papel la imprime); las filas fijas vienen con su nombre impreso y las libres se rotulan a mano; en el coprológico los protozoarios
// (quiste/trofozoíto) y helmintos (huevo/larva) son filas libres; la hoja 2 ofrece la capacidad del papel por sección (ver `CAPACIDAD_HOJA2_010B`).
// Unidad: solo la sección Química la imprime (columna); los datos viejos con unidad en otra sección se siguen leyendo y saliendo en el PDF (`filasConResultado` no depende de la columna).
// Filas libres de hoja 1 (espacio en blanco al pie de cada caja): Hematológico 1, Coprológico 1, además de las que ya había.
//
// FHIR (consumidor: `LabsTab` lee `Observation.code.text`/`valueString`/`referenceRange[0].text` y
// `DiagnosticReport.conclusion`): un `Observation` por fila con resultado, y el `DiagnosticReport` que los agrupa.
import { conCodigoCie, paresDeCampos, valoresVacios, type CampoSimple, type OpcionCampo, type Valores } from './camposSimplesDatos';
import type { DiagnosticoCie10Valor } from './SelectorDiagnosticoCie10';
import type { ColumnaTablaConColumnas, EstadoTablaConColumnas, FilaTablaConColumnas } from './uci/TablaConColumnas';
import type { Par } from './index';

const opciones = (...e: readonly string[]): readonly OpcionCampo[] => e.map((x) => ({ valor: x, etiqueta: x }));

export const CAMPOS_SERVICIO_010B: readonly CampoSimple[] = [
  { clave: 'servicio', etiqueta: 'Servicio', tipo: 'select', opciones: opciones('Emergencia', 'Consulta externa', 'Hospitalización') },
  { clave: 'especialidad', etiqueta: 'Especialidad' },
  { clave: 'sala', etiqueta: 'Sala' },
  { clave: 'cama', etiqueta: 'Cama' },
  { clave: 'prioridad', etiqueta: 'Prioridad', tipo: 'select', opciones: opciones('Urgente', 'Rutina') },
];

export const COLUMNAS_010B: readonly ColumnaTablaConColumnas[] = [
  { clave: 'examen', etiqueta: 'Examen' },
  { clave: 'resultado', etiqueta: 'Resultado' },
  { clave: 'unidad', etiqueta: 'Unidad de medida' },
  { clave: 'referencia', etiqueta: 'Valor de referencia' },
];

export interface SeccionResultados {
  readonly clave: string;
  readonly etiqueta: string;
  /** Nombres impresos (filas fijas, con su unidad si el papel la rotula). */
  readonly fijas: readonly string[];
  /** Filas libres al final (se rotulan a mano). */
  readonly libres: number;
  /** El papel imprime la columna "Unidad de medida" solo en Química (hoja 1); en las demás secciones la unidad va dentro del resultado. */
  readonly unidad: boolean;
}

const s = (clave: string, etiqueta: string, fijas: readonly string[], libres: number, unidad = false): SeccionResultados => ({ clave, etiqueta, fijas, libres, unidad });

export const SECCIONES_HOJA1_010B: readonly SeccionResultados[] = [
  s(
    'hematologico',
    'Hematológico',
    [
      'HCTO %', 'HB g/dl', 'VCM', 'Reticulocitos', 'Velocidad de sedimentación (mmh)', 'HCM', 'Drepanocitos', 'Plaquetas (mmc)', 'CHCM', 'Grupo - Factor Rh', 'Leucocitos (mmc)', 'Hipocromia',
      'Coombs directo', 'Metam %', 'Basof %', 'Anisocitosis', 'Coombs indirecto', 'Cayad %', 'Monoc %', 'Poiquilocit.', 'Tiempo de sangría', 'Segme %', 'Linfo %', 'Microcitosis',
      'Tiempo de protrombina (seg)', 'Eosin %', 'Atipi %', 'Policromat.', 'Tiempo T. parcial (seg)',
    ],
    1,
  ),
  s('coprologico', 'Coprológico', ['Color', 'Consistencia', 'pH', 'Hemoglobina', 'Glóbulos rojos', 'Polimorfos', 'Esporas', 'Micelios', 'Moco', 'Fibras', 'Almidón', 'Grasa'], 1),
  s('protozoarios', 'Coprológico — Protozoarios (quiste / trofozoíto)', [], 4),
  s('helmintos', 'Coprológico — Helmintos (huevo / larva)', [], 4),
  s('uroElemental', 'Uroanálisis — Elemental', ['Densidad', 'pH', 'Proteína', 'Glucosa', 'Cetona', 'Hemoglobina', 'Bilirrubina', 'Urobilinógeno', 'Nitrito', 'Leucocitos'], 2),
  s('uroMicroscopico', 'Uroanálisis — Microscópico', ['Leucocitos por campo', 'Piocitos por campo', 'Eritrocitos por campo', 'Células altas', 'Bacterias', 'Hongos', 'Moco', 'Cristales', 'Cilindros'], 1),
  s(
    'quimica',
    'Química',
    [
      'Glucosa en ayunas', 'Glucosa 2 horas', 'Urea', 'Creatinina', 'Bilirrubina total', 'Bilirrubina directa', 'Ácido úrico', 'Proteína total', 'Albúmina', 'Globulina',
      'Transaminasa pirúvica (ALT)', 'Transaminasa oxalacética (AST)', 'Fosfatasa alcalina', 'Fosfatasa ácida', 'Colesterol total', 'Colesterol HDL', 'Colesterol LDL', 'Triglicéridos', 'Hierro sérico', 'Amilasa',
    ],
    2,
    true,
  ),
  s('serologia', 'Serología', ['VDRL', 'Látex', 'Aglutinaciones febriles', 'ASTO'], 2),
  s('bacteriologia', 'Bacteriología', [], 4),
  s('otros', 'Otros', [], 4),
];

/**
 * Hoja 2: cada sección trae dos paneles Examen / Resultado / Valores de referencia lado a lado; las filas por panel son las contadas en el escaneo
 * (idx 46, de Hematología a Biología molecular). Aquí cada fila es un panel, así que la capacidad de la sección es 2 × filas. La capacidad es exactamente la del papel
 * (sin piso): Inmunosupresores y Medicina transfusional traen 2 filas por panel. Sin columna "Unidad" (el papel no
 * la imprime en la hoja 2; la unidad se escribe en el resultado).
 */
const CAPACIDAD_HOJA2_010B: readonly (readonly [string, number])[] = [
  ['Hematología', 4], ['Química sanguínea', 7], ['Inmunología infecciosas', 6], ['Marcadores cardiovasculares', 4], ['Hormonas', 6], ['Coagulación y hemostasia', 5], ['Orina', 8],
  ['Inmunosupresores', 2], ['Gases y electrolitos', 5], ['Medicina transfusional', 2], ['Heces', 5], ['Marcadores tumorales', 5], ['Citoquímico y bacteriológico de líquidos', 3],
  ['Niveles de fármacos terapéuticos', 4], ['Serología', 4], ['Microbiología', 4], ['Biología molecular y genética', 3],
];

export const SECCIONES_HOJA2_010B: readonly SeccionResultados[] = CAPACIDAD_HOJA2_010B.map(([etiqueta, filas], i) => s(`h2_${i}`, etiqueta, [], filas * 2));

export const SECCIONES_010B: readonly SeccionResultados[] = [...SECCIONES_HOJA1_010B, ...SECCIONES_HOJA2_010B];

/** Columnas de una sección: sin "Unidad de medida" donde el papel no la imprime. */
export const columnasDeSeccion = (sec: SeccionResultados): readonly ColumnaTablaConColumnas[] => (sec.unidad ? COLUMNAS_010B : COLUMNAS_010B.filter((c) => c.clave !== 'unidad'));

export const filasDeSeccion = (sec: SeccionResultados): readonly FilaTablaConColumnas[] =>
  Array.from({ length: sec.fijas.length + sec.libres }, (_, i) => ({ clave: `f${i}`, etiqueta: String(i + 1) }));

export interface Estado010B {
  readonly diagnostico: DiagnosticoCie10Valor;
  readonly servicio: Valores;
  /** Una tabla por sección; los nombres de las filas fijas vienen precargados en la columna Examen. */
  readonly resultados: Readonly<Record<string, EstadoTablaConColumnas>>;
}

export function estadoInicial010B(): Estado010B {
  const resultados = Object.fromEntries(
    SECCIONES_010B.map((sec) => [
      sec.clave,
      Object.fromEntries(filasDeSeccion(sec).map((f, i) => [f.clave, { examen: sec.fijas[i] ?? '', resultado: '', unidad: '', referencia: '' }])),
    ]),
  );
  return { diagnostico: { texto: '', cie10: null }, servicio: valoresVacios(CAMPOS_SERVICIO_010B), resultados };
}

export interface FilaResultado {
  readonly seccion: string;
  readonly examen: string;
  readonly resultado: string;
  readonly unidad: string;
  readonly referencia: string;
}

/** Filas con resultado capturado (el nombre preimpreso solo no cuenta). */
export function filasConResultado(e: Estado010B): FilaResultado[] {
  return SECCIONES_010B.flatMap((sec) =>
    filasDeSeccion(sec).flatMap((f) => {
      const r = e.resultados[sec.clave]?.[f.clave];
      if (!r) return [];
      const fila: FilaResultado = { seccion: sec.etiqueta, examen: (r.examen ?? '').trim(), resultado: (r.resultado ?? '').trim(), unidad: (r.unidad ?? '').trim(), referencia: (r.referencia ?? '').trim() };
      return fila.resultado ? [fila] : [];
    }),
  );
}

/** Valor legible del resultado: "resultado unidad". */
export const valorConUnidad = (f: FilaResultado): string => [f.resultado, f.unidad].filter(Boolean).join(' ');

export function paresDe010B(e: Estado010B): Par[] {
  return [
    { pregunta: 'Diagnóstico', respuesta: conCodigoCie(e.diagnostico) },
    ...paresDeCampos(CAMPOS_SERVICIO_010B, e.servicio),
    ...filasConResultado(e).map((f) => ({
      pregunta: `${f.seccion} — ${f.examen || 'Sin rótulo'}`,
      respuesta: [valorConUnidad(f), f.referencia ? `(referencia: ${f.referencia})` : ''].filter(Boolean).join(' '),
    })),
  ].filter((p) => p.respuesta.trim() !== '');
}
